import { useCallback, useMemo, useState } from "react";
import { styled } from "styled-components";

interface Props {
  limit?: number;
  offset?: number;
  fillLast?: boolean;
}

//@ts-ignore
const itemWithId = (offset: number) => (x, i) => ({
  ...x,
  _id: `item-${offset + i}`,
});

export function useCarousel<T extends object>(inputItems: T[], props: Props) {
  const { limit = 1, offset = 1, fillLast } = props;

  const addIdsToFilledLast = inputItems.length < limit;
  const sliceItems = useMemo(() => inputItems.map(itemWithId(0)), [inputItems]);
  const [sliceFrom, setSlice] = useState(0);
  const dotLength = Math.ceil(sliceItems.length / offset);

  const prev = useCallback(() => {
    setSlice(getPrev(sliceItems.length, offset));
  }, [sliceItems.length, offset]);

  const next = useCallback(() => {
    setSlice(getNext(sliceItems.length, offset));
  }, [sliceItems.length, offset]);

  const items: T[] = useMemo(() => {
    const ret = sliceItems.slice(sliceFrom, sliceFrom + limit);
    if (fillLast && ret.length < limit) {
      let append = sliceItems.slice(0, limit - ret.length);
      if (addIdsToFilledLast) {
        append = append.map(itemWithId(sliceItems.length));
      }
      ret.push(...append);
    }
    return ret;
  }, [addIdsToFilledLast, sliceItems, sliceFrom, fillLast, limit]);

  const active = useMemo(() => sliceFrom / offset, [sliceFrom, offset]);
  const dots = useMemo(
    () => new Array(dotLength).fill(0).map((_, i) => offset * i),
    [dotLength, offset]
  );

  const currentSlide: T = items[1];

  return useMemo(
    () => ({
      next,
      prev,
      items,
      active,
      dots,
      setSlice,
      currentSlide,
    }),
    [prev, next, items, active, dots, setSlice, currentSlide]
  );
}

function getPrev(length: number, offset: number) {
  return (sliceFrom: number) => {
    let to = sliceFrom - offset;
    if (to < 0) {
      const mod = length % offset;
      return length - (mod || offset);
    }
    return to;
  };
}

function getNext(length: number, offset: number) {
  return (sliceFrom: number) => {
    const to = sliceFrom + offset;
    if (to >= length) return 0;
    return to;
  };
}

export interface DotsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">,
    Pick<DotProps, "showBorder"> {
  active: number;
  dots: number[];
  setSlice: (index: number) => void;
}

export const Dots: React.FC<DotsProps> = ({
  dots,
  active,
  setSlice,
  showBorder,
  ...rest
}) => (
  <DotWrapper {...rest}>
    {dots.map((dot, i) => (
      <Dot
        key={i}
        active={i === active}
        onClick={() => setSlice(dot)}
        showBorder={showBorder}
      />
    ))}
  </DotWrapper>
);

export const DotWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

export type DotProps = { active: boolean; showBorder?: boolean };
export const Dot = styled.button<DotProps>`
  cursor: pointer;
  padding: 0;
  background: ${(props) => (props.active ? "var(--swatch1)" : "#d9d9d9")};
  border: ${(props) =>
    props.showBorder ? "1px solid var(--swatch2)" : "none"};
  margin: 0 ${(props) => props.theme.spacing.xs};
  width: 15px;
  height: 15px;
  border-radius: 50%;
`;
